import {
  ResponsiveContainer,
  LineChart,
  XAxis,
  YAxis,
  Line,
  Tooltip,
  LineProps,
} from "recharts";
import { formatWithCommas } from "../../../../utils/formatter";
import React, { useEffect, useState } from "react";

type Props = {
  commodity: string;
  baseSKU: string;
  data: Record<string, any>[];
  name: string;
  vendors: string[];
  partnerVendor: string;
  unit: string;
  isEstimated: boolean;
  decimal: number;
  isdownload: boolean;
  trendView?: string;
};

export default function LineChartGraph({
  commodity,
  baseSKU,
  data,
  name,
  unit = "",
  vendors = [],
  partnerVendor = "",
  isEstimated = false,
  decimal = 0,
  isdownload = false,
  trendView = "",
}: Props) {
  const vendorGraphTheme = {
    ATKORE: "#62b5e5",
    ZEKELMAN: "#012169",
    NUCOR: "#046A38",
    IPEX: "#9c765f",
    "PRIME CONDUIT": "#8cd17e",
    CANTEX: "#0097a9",
    "NATIONAL PIPE": "#FF921D",
    "SOUTHERN PIPE": "#b17aa1",
    "Other Suppliers": "#bcbcbc",
  };

  const wireVendorGraphTheme = {
    "ALAN WIRE": "#a0dbfe",
    CERROWIRE: "#e97132",
    "CME WIRE": "#da291c",
    "COLONIAL WIRE": "#007cb0",
    ENCORE: "#50b539",
    "REPUBLIC WIRE": "#9c765f",
    "SERVICE WIRE": "#041e42",
    SOUTHWIRE: "#a02b93",
    OMNICABLE: "#203c74",
    "PRIORITY WIRE": "#ffc000",
    "Other Suppliers": "#bcbcbc",
  };

  const [ticks, setTicks] = useState<number[]>([]);

  const isWeekly = (trendView || "").toLowerCase() === "weekly";

  // Strip the trailing "-YY" year (e.g. "Aug 04-26" -> "Aug 04") for weekly axis labels
  const stripYear = (m: string) =>
    m && m.lastIndexOf("-") > 0 ? m.substring(0, m.lastIndexOf("-")) : m;

  const getMinMaX = () => {
    const values = [];
    for (const entry of data) {
      const boardCost = entry["Board Cost"];
      if (boardCost != null && boardCost !== 0) {
        values.push(boardCost);
      }
      for (const vendor of vendors) {
        const actual = entry[`${vendor}_actualPoCost`];
        const estimated = entry[`${vendor}_estimatedPoCost`];
        if (actual != null && actual !== 0) {
          values.push(actual);
        } else if (estimated != null && estimated !== 0) {
          values.push(estimated);
        }
      }
    }
    if (values.length === 0) return { min: null, max: null };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const low = Math.floor(min);
    const above = Math.ceil(max);
    const interval = Math.floor((max - low) / 4);
    const arr = [
      low,
      low + interval * 1,
      low + interval * 2,
      low + interval * 3,
      // low + interval * 4,
      above + 2,
    ];
    setTicks(arr);
  };

  useEffect(() => {
    getMinMaX();
  }, [data]);

  const getCenteredTrianglePoints = (
    x: any,
    y: any,
    base: any,
    height: any,
  ) => {
    return [
      `${x},${y - height}`, // Top
      `${x + base / 2},${y + height}`, // Bottom Right
      `${x - base / 2},${y + height}`, // Bottom Left
    ].join(" ");
  };

  const getCenteredRhombusPoints = (
    x: number,
    y: number,
    width: number,
    height: number,
  ): string => {
    return [
      `${x},${y - height / 2}`, // Top
      `${x + width / 2},${y}`, // Right
      `${x},${y + height / 2}`, // Bottom
      `${x - width / 2},${y}`, // Left
    ].join(" ");
  };

  return (
    <div className="shadow-module-card bg-white w-full h-full">
      <div className="font-bold pl-[1.2vh] pt-[1.2vh] pb-[0.5vh] text-[1.9vh]">
        <>
          <span>{name}</span>
          {partnerVendor !== "" && (
            <span>
              {" "}
              for <span className="text-[#86BC25]">{partnerVendor}</span>
            </span>
          )}
        </>
      </div>
      <div className="flex flex-wrap items-start h-10">
        {vendors?.map((ele, i) => (
          <div key={ele} className={`flex items-center ml-[2vh]`}>
            <div
              style={{
                backgroundColor:
                  commodity === "Copper Wire"
                    ? wireVendorGraphTheme[
                        ele as keyof typeof wireVendorGraphTheme
                      ]
                    : vendorGraphTheme[ele as keyof typeof vendorGraphTheme],
              }}
              className={`lengends h-[0.5vh] w-[1vw] rounded-[1vh] mr-[0.5vh]`}
            ></div>
            <div className="text-[1.4vh]">{ele}</div>
          </div>
        ))}
        <div className={`flex items-center ml-[2vh]`}>
          <div
            className={`lengends h-[0.5vh] w-[0.5vw] rounded-[1vh] mr-[0.5vh] bg-[black]`}
          />
          <div
            className={`lengends h-[0.5vh] w-[0.5vw] rounded-[1vh] mr-[0.5vh] bg-[black]`}
          />
          <div className="text-[1.6vh]">Board Cost</div>
        </div>
      </div>
      {/* <div className="flex pt-1">
        {vendors?.slice((vendors.length + 1) / 2).map((ele, i) => (
          <div key={ele} className={`flex items-center ml-[2vh]`}>
            <div
              style={{
                backgroundColor:
                  commodity === "Copper Wire"
                    ? wireVendorGraphTheme[
                        ele as keyof typeof wireVendorGraphTheme
                      ]
                    : vendorGraphTheme[ele as keyof typeof vendorGraphTheme],
              }}
              className={`lengends h-[0.5vh] w-[1vw] rounded-[1vh] mr-[0.5vh]`}
            ></div>
            <div className="text-[1.4vh]">{ele}</div>
          </div>
        ))}
        <div className={`flex items-center ml-[2vh]`}>
          <div
            className={`lengends h-[0.5vh] w-[0.5vw] rounded-[1vh] mr-[0.5vh] bg-[black]`}
          />
          <div
            className={`lengends h-[0.5vh] w-[0.5vw] rounded-[1vh] mr-[0.5vh] bg-[black]`}
          />
          <div className="text-[1.6vh]">Board Cost</div>
        </div>
      </div> */}
      <div className="h-[85%] w-full">
        <ResponsiveContainer height="92%">
          <LineChart style={{ padding: "1vh 1vh 1vh 0vh" }} data={data}>
            <XAxis clip={"none"} height={42} padding={{ left: 20 }} dataKey="month" tickFormatter={(value) => (isWeekly ? stripYear(value) : value)} />
            <YAxis
              ticks={ticks}
              domain={([DataMin, DataMax]) => [DataMin - 2, DataMax]}
              tickFormatter={(value) => {
                return unit + value;
              }}
              hide={isdownload}
            />
            <Tooltip
              formatter={(value: any, name: any, props: any) => {
                const { dataKey, payload } = props;
                if (typeof dataKey === "function") {
                  return [
                    `$${formatWithCommas(
                      parseFloat(value)?.toFixed(decimal)?.toString(),
                    )}`,
                  ];
                }
                const vendor = vendors.find((v) => dataKey?.includes(v));
                const lable = dataKey;
                return [
                  `$${formatWithCommas(
                    parseFloat(value)?.toFixed(decimal)?.toString(),
                  )}`,
                ];
              }}
            />
            <Line
              type="linear"
              dataKey="Board Cost"
              stroke="black"
              strokeDasharray="10 10"
              dot={{ r: 2.2, fill: "black" }}
              activeDot={{ r: 4, fill: "black" }}
              // label={{
              //   position: "top",
              //   fill: "black",
              //   fontSize: 11,
              //   formatter: (value: any) => {
              //     return isdownload
              //       ? `$${formatWithCommas(
              //           parseFloat(value)?.toFixed(decimal)?.toString()
              //         )}`
              //       : "";
              //   },
              // }}
              label={(props) => {
                const { x, y, value } = props;

                // Format label text
                const labelText = `$${formatWithCommas(
                  parseFloat(value)?.toFixed(decimal)?.toString(),
                )}`;
                // Estimate text width and height
                const fontSize = 11;
                const paddingX = 0;
                const paddingY = 0;
                const textWidth =
                  labelText.length * fontSize * 0.6 + paddingX * 2;
                const textHeight = fontSize + paddingY * 2;

                // Adjust position for label above the point
                const labelY = y - 18; // or tweak as needed

                return (
                  <>
                    {isdownload && commodity === "Steel Conduit" && (
                      <>
                        <rect
                          x={x - textWidth / 2}
                          y={labelY - textHeight / 10}
                          width={textWidth}
                          height={textHeight}
                          fill="#fff"
                          rx={3}
                          opacity={0.75}
                        />
                        <text
                          x={x}
                          y={labelY}
                          fill="black"
                          fontSize={fontSize}
                          textAnchor="middle"
                          alignmentBaseline="hanging"
                          style={{ pointerEvents: "none" }}
                        >
                          {labelText}
                        </text>
                      </>
                    )}
                  </>
                );
              }}
            />
            {vendors.map((ele, i) => (
              <Line
                key={i}
                type="linear"
                dataKey={(entry) =>
                  entry[`${ele}_actualPoCost`] !== null
                    ? entry[`${ele}_actualPoCost`]
                    : entry[`${ele}_estimatedPoCost`]
                }
                stroke={
                  commodity === "Copper Wire"
                    ? wireVendorGraphTheme[
                        ele as keyof typeof wireVendorGraphTheme
                      ]
                    : vendorGraphTheme[ele as keyof typeof vendorGraphTheme]
                }
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  const isEstimated = payload[`${ele}_actualPoCost`];
                  const color = !isEstimated
                    ? "#E15759"
                    : commodity === "Copper Wire"
                      ? wireVendorGraphTheme[
                          ele as keyof typeof wireVendorGraphTheme
                        ]
                      : vendorGraphTheme[ele as keyof typeof vendorGraphTheme];
                  return isEstimated ? (
                    <circle cx={cx} cy={cy} r={2.6} fill={color} />
                  ) : payload[`${ele}_estimatedPoCost`] !== null ? (
                    payload[`${ele}_estimatedPOMethod`] !==
                    "Vendor Quotes Based" ? (
                      <svg
                        width="20"
                        height="20"
                        x={cx - 10}
                        y={cy - 10}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polygon
                          points={getCenteredRhombusPoints(10, 10, 8, 8)}
                          fill={color}
                          stroke={color}
                          strokeWidth="1"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        x={cx - 10}
                        y={cy - 10}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polygon
                          points={getCenteredTrianglePoints(10, 10, 6, 3)}
                          fill={color}
                          stroke={color}
                          stroke-width="1"
                        />
                      </svg>
                    )
                  ) : (
                    <></>
                  );
                }}
                activeDot={(props: any) => {
                  const { cx, cy, payload } = props;
                  const isEstimated = payload[`${ele}_actualPoCost`];
                  const color = !isEstimated
                    ? "#E15759"
                    : commodity === "Copper Wire"
                      ? wireVendorGraphTheme[
                          ele as keyof typeof wireVendorGraphTheme
                        ]
                      : vendorGraphTheme[ele as keyof typeof vendorGraphTheme];
                  return isEstimated ? (
                    <circle cx={cx} cy={cy} r={2.6} fill={color} />
                  ) : payload[`${ele}_estimatedPoCost`] !== null ? (
                    payload[`${ele}_estimatedPOMethod`] !==
                    "Vendor Quotes Based" ? (
                      <svg
                        width="20"
                        height="20"
                        x={cx - 10}
                        y={cy - 10}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polygon
                          points={getCenteredRhombusPoints(10, 10, 8, 8)}
                          fill={color}
                          stroke={color}
                          strokeWidth="1"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        x={cx - 10}
                        y={cy - 10}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polygon
                          points={getCenteredTrianglePoints(10, 10, 6, 3)}
                          fill={color}
                          stroke={color}
                          stroke-width="1"
                        />
                      </svg>
                    )
                  ) : (
                    <></>
                  );
                }}
                label={(props) => {
                  const { x, y, value, index } = props;
                  const isEstimated = data[index][`${ele}_actualPoCost`];
                  const color = !isEstimated
                    ? "#E15759"
                    : commodity === "Copper Wire"
                      ? wireVendorGraphTheme[
                          ele as keyof typeof wireVendorGraphTheme
                        ]
                      : vendorGraphTheme[ele as keyof typeof vendorGraphTheme];
                  let labelText = "";
                  if (isEstimated) {
                    labelText = `$${formatWithCommas(
                      parseFloat(value)?.toFixed(decimal)?.toString(),
                    )}`;
                  } else if (data[index][`${ele}_estimatedPoCost`] !== null) {
                    labelText = `$${formatWithCommas(
                      parseFloat(value)?.toFixed(decimal)?.toString(),
                    )}`;
                  }

                  // Estimate text width (fontSize * chars * factor)
                  const fontSize = 11;
                  const paddingX = 0; // horizontal padding
                  const paddingY = 0; // vertical padding
                  const textWidth =
                    labelText.length * fontSize * 0.6 + paddingX * 2;
                  const textHeight = fontSize + paddingY * 2;

                  // Adjust y position for "Other Vendors"
                  const labelY = ele === "Other Suppliers" ? y - 18 : y + 6;
                  return (
                    // <text
                    //   x={x}
                    //   y={ele === "Other Vendors" ? y - 18 : y + 6}
                    //   fill={color}
                    //   fontSize={11}
                    //   textAnchor="middle"
                    //   alignmentBaseline="hanging"
                    // >
                    //   {
                    //     // isdownload
                    //     // ?
                    //     isEstimated
                    //       ? `$${formatWithCommas(
                    //           parseFloat(value)?.toFixed(decimal)?.toString()
                    //         )}`
                    //       : data[index][`${ele}_estimatedPoCost`] !== null
                    //       ? `$${formatWithCommas(
                    //           parseFloat(value)?.toFixed(decimal)?.toString()
                    //         )}`
                    //       : ""
                    //     // : ""
                    //   }
                    // </text>
                    <>
                      {/* White rectangle background */}
                      {isdownload && commodity === "Steel Conduit" && (
                        <>
                          <rect
                            x={x - textWidth / 2}
                            y={labelY - textHeight / 10}
                            width={textWidth}
                            height={textHeight}
                            fill="#fff"
                            rx={3} // rounded corners (optional)
                            opacity={0.75} // slightly transparent for style
                          />
                          {/* Label text */}
                          <text
                            x={x}
                            y={labelY}
                            fill={color}
                            fontSize={fontSize}
                            textAnchor="middle"
                            alignmentBaseline="hanging"
                            style={{ pointerEvents: "none" }}
                          >
                            {labelText}
                          </text>
                        </>
                      )}
                    </>
                  );
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div
        className={`flex font-bold text-[1.4vh] w-full h-[5%] justify-start items-center gap-4 pl-2 ${
          !isEstimated && "hidden"
        }`}
      >
        <span className="inline-flex items-center leading-none">
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <polygon
              points="10,6 14,14 6,14"
              fill="#E15759"
              stroke="#E15759"
              strokeWidth="1"
            />
          </svg>
          <span className="ml-1">
            Estimated using supplier quote for&nbsp;{baseSKU}
          </span>
        </span>
        <span className="inline-flex items-center leading-none">
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <polygon
              points="10,6 14,10 10,14 6,10"
              fill="#E15759"
              stroke="#E15759"
              strokeWidth="1"
            />
          </svg>
          <span className="ml-1">Estimated using SKU ratio analysis</span>
        </span>
      </div>
    </div>
  );
}
